/* 편집 화면 — 요소 그리기 · 선택 · 끌어서 옮기기 · 크기(손잡이 8개) · 회전 · 스마트 정렬선 · 글 직접 고치기 · 요소 첨부(◎ 선택) */
import { IcAi, IcChat, IcClose, IcPen } from './icons';
import { memo, useEffect, useLayoutEffect, useRef, useState, type MouseEvent as RMouseEvent } from 'react';
import { Button } from '@indipia/ui-core';
import { useImageUrl } from '../model/images';
import { PAGE_H, PAGE_W, type El } from '../model/types';
import {
  bringToFront, currentPage, deleteElement, duplicateSelected, ed, findElement, rememberUndo, select, sendToBack, useEd,
} from '../store/editor';
import { bodyClass, bodyStyle, outerStyle, ShapeInner } from './render';
import { cx } from './ui';

/* 크기 손잡이 — 모서리는 동그라미, 변은 막대 */
const RZ = 'absolute rounded-full border-thick border-solid !border-group bg-[var(--gray-0)] shadow-[var(--elevation-1)]';
const RZ_AT: Record<string, string> = {
  nw: 'size-04 -left-03 -top-03 cursor-nwse-resize', se: 'size-04 -bottom-03 -right-03 cursor-nwse-resize',
  ne: 'size-04 -right-03 -top-03 cursor-nesw-resize', sw: 'size-04 -bottom-03 -left-03 cursor-nesw-resize',
  n: 'left-1/2 -top-02 h-03 w-06 -translate-x-1/2 rounded-sm cursor-ns-resize', s: 'left-1/2 -bottom-02 h-03 w-06 -translate-x-1/2 rounded-sm cursor-ns-resize',
  e: 'top-1/2 -right-02 h-06 w-03 -translate-y-1/2 rounded-sm cursor-ew-resize', w: 'top-1/2 -left-02 h-06 w-03 -translate-y-1/2 rounded-sm cursor-ew-resize',
};
const BADGE = 'whitespace-nowrap rounded-sm bg-[var(--dusty-rose-500)] px-03 py-01 text-caption font-semibold text-[var(--gray-0)]';

export const ROT_ICON = (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M15.5 6.5A6.5 6.5 0 1 0 16.5 11" /><path d="M16 2.5v4h-4" /></svg>
);
const PAGE_WHITE = '#FFFFFF'; // dq-ignore: 문서 페이지 기본 배경(내보낸 사진·공개 페이지와 같아야 함)
const SNAP_PX = 6;
const PAGE_MARGIN = 48;
const normDeg = (d: number) => { d = (((d + 180) % 360) + 360) % 360 - 180; return Math.round(d * 10) / 10; };

interface Target { v: number }
function snapTargets(elm: El) {
  const xs: Target[] = [0, PAGE_MARGIN, PAGE_W / 2, PAGE_W - PAGE_MARGIN, PAGE_W].map((v) => ({ v }));
  const ys: Target[] = [0, PAGE_MARGIN, PAGE_H / 2, PAGE_H - PAGE_MARGIN, PAGE_H].map((v) => ({ v }));
  const others = (currentPage()?.elements || []).filter((o) => o !== elm);
  others.forEach((o) => {
    [o.x, o.x + o.w / 2, o.x + o.w].forEach((v) => xs.push({ v }));
    [o.y, o.y + o.h / 2, o.y + o.h].forEach((v) => ys.push({ v }));
  });
  return { xs, ys, others };
}
function bestSnap(points: number[], targets: Target[], limit: number) {
  let best: { d: number; v: number } | null = null;
  points.forEach((p) => targets.forEach((t) => {
    const d = t.v - p;
    if (Math.abs(d) <= limit && (!best || Math.abs(d) < Math.abs(best.d))) best = { d, v: t.v };
  }));
  return best as { d: number; v: number } | null;
}
const uniq = (arr: number[]) => arr.filter((v, i) => arr.findIndex((w) => Math.abs(w - v) < 0.5) === i);

interface Guides { elm: El; vx: number[]; hy: number[]; badges: { text: string; x: number; y: number }[] }
/** saved: 실제로 움직였을 때 한 번만 되돌리기 지점을 남긴다(눌렀다 떼기만 하면 남기지 않음) */
type Op = { saved?: boolean } & (
  | { kind: 'drag'; id: string; sx: number; sy: number; ox: number; oy: number }
  | { kind: 'resize'; id: string; dir: string; sx: number; sy: number; ow: number; oh: number; ox: number; oy: number }
  | { kind: 'rotate'; id: string; cx: number; cy: number; start: number; base: number });

/** 글 상자 — 직접 고칠 수 있게. 고치는 중엔 React가 글을 다시 넣지 않는다(커서가 튀지 않게) */
function TextBody({ elm, editable }: { elm: El; editable: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const b = ref.current;
    if (b && document.activeElement !== b && b.textContent !== (elm.text || '')) b.textContent = elm.text || '';
  });
  return (
    <div
      ref={ref}
      className={cx(bodyClass(elm), ed.typing.has(elm.id) && "bg-[var(--alpha-white-300)] after:animate-pulse after:text-group after:content-['▍']", ed.thinking.has(elm.id) && 'opacity-30')}
      style={bodyStyle(elm)}
      contentEditable={editable}
      suppressContentEditableWarning
      spellCheck={false}
      onMouseDown={(e) => { if (ed.picking) return; select(elm.id); e.stopPropagation(); }}
      onBlur={(e) => {
        const t = e.currentTarget.innerText;
        if (t !== elm.text) { rememberUndo(elm.id + ':text'); elm.text = t; ed.bump(); }
      }}
    />
  );
}

const PageEl = memo(function PageEl({ elm, rev, onGrab }: { elm: El; rev: number; onGrab: (e: RMouseEvent, elm: El) => void }) {
  void rev; // 문서를 직접 고치므로 rev 로 다시 그린다
  const url = useImageUrl(elm.type === 'image' ? elm.src : undefined, false);
  const attachNo = ed.attached.indexOf(elm) + 1;
  const imaging = ed.imaging.has(elm.id);
  const cls = cx('page-el absolute',
    attachNo ? 'outline outline-2 -outline-offset-2 outline-[var(--status-warning-500)]' : elm.id === ed.selectedId && 'outline outline-2 outline-offset-1 outline-group',
    ed.flash.has(elm.id) && 'shadow-[0_0_0_3px_var(--neutral-200)] transition-shadow',
    ed.picking && 'hover:outline-dashed hover:outline-2 hover:-outline-offset-2 hover:outline-[var(--status-warning-500)]');
  return (
    <div className={cls} style={outerStyle(elm)} data-id={elm.id}>
      {elm.type === 'text' ? (
        <TextBody elm={elm} editable={!ed.picking} />
      ) : (
        <div className={bodyClass(elm)} style={bodyStyle(elm, url)} onMouseDown={(e) => onGrab(e, elm)}>
          {elm.type === 'shape' && <ShapeInner elm={elm} />}
        </div>
      )}
      {ed.thinking.has(elm.id) && (
        // 글이 오기 전 — AI가 생각하는 동안 글 상자에 안내. 화면 확대·축소와 상관없이 같은 크기로
        <div className="pointer-events-none absolute left-0 top-0 origin-top-left" style={{ transform: `scale(${1 / ed.zoom})` }} role="status">
          <span className="inline-flex animate-pulse items-center gap-xs whitespace-nowrap rounded-full bg-group-subtle px-03 py-01 text-caption font-semibold text-group-onSubtle shadow-[var(--elevation-1)]">
            <IcAi size={16} />AI가 생각하는 중… · {ed.thinking.get(elm.id)}
          </span>
        </div>
      )}
      {imaging && (
        <div className="pointer-events-none absolute inset-0 animate-pulse bg-[var(--alpha-black-300)]" aria-hidden>
          <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded-md bg-[var(--alpha-black-800)] px-04 py-03 text-body-sm font-semibold text-[var(--gray-0)]">AI가 사진을 고치는 중…</span>
        </div>
      )}
      {!!attachNo && <div className={cx('pointer-events-none absolute flex size-05 items-center justify-center rounded-full bg-[var(--status-warning-500)] text-caption font-bold text-[var(--gray-0)] shadow-[var(--elevation-1)]', elm.x < 10 || elm.y < 10 ? 'left-02 top-02' : '-left-03 -top-03')}>{attachNo}</div>}
    </div>
  );
});

/* AI가 쓴 글이 상자보다 길면 — 먼저 아래 빈 공간까지 상자를 늘리고, 그래도 넘치면 글자를 조금씩 줄인다(최소 12.5px) */
function fitTextBoxes(board: HTMLElement) {
  const page = currentPage();
  if (!page) return;
  page.elements.forEach((el) => {
    if (el.type !== 'text' || !el.text) return;
    const div = board.querySelector<HTMLElement>(`.page-el[data-id="${el.id}"]`);
    const body = div?.querySelector<HTMLElement>('.el-text-body');
    if (!div || !body || body.scrollHeight <= body.clientHeight + 1) return;
    const below = page.elements.filter((o) => o !== el && o.y >= el.y + el.h - 2 && o.x < el.x + el.w && o.x + o.w > el.x);
    const limit = Math.min(PAGE_H - 36, ...below.map((o) => o.y - 12));
    const h = Math.min(Math.max(el.h, body.scrollHeight + 2), Math.max(el.h, limit - el.y));
    if (h !== el.h) { el.h = h; div.style.height = h + 'px'; }
    let fs = el.fontSize || 16;
    while (body.scrollHeight > body.clientHeight + 1 && fs > 12.5) {
      fs = Math.max(12.5, Math.round((fs - 0.5) * 10) / 10);
      body.style.fontSize = fs + 'px';
    }
    el.fontSize = fs;
  });
}

export function Artboard({ onMarkup }: { onMarkup: (elm: El) => void }) {
  const e = useEd();
  const page = currentPage();
  const boardRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const op = useRef<Op | null>(null);
  const [guides, setGuides] = useState<Guides | null>(null);
  const [rotBadge, setRotBadge] = useState<{ x: number; y: number; deg: number } | null>(null);
  const [tb, setTb] = useState<{ left: number; top: number } | null>(null);
  const [moving, setMoving] = useState(false);

  useLayoutEffect(() => { if (boardRef.current) fitTextBoxes(boardRef.current); });

  // 처음 열 때 편집 칸이 페이지보다 좁으면 맞춰서 축소
  useEffect(() => {
    const w = scrollRef.current?.clientWidth || 0;
    if (w && w - 48 < PAGE_W) { ed.zoom = Math.max(0.4, Math.floor(((w - 48) / PAGE_W) * 10) / 10); ed.bump(); }
  }, []);

  // 선택한 요소 위 도구 막대 위치 (화면 좌표)
  useLayoutEffect(() => {
    const place = () => {
      const div = e.selectedId ? boardRef.current?.querySelector<HTMLElement>(`.page-el[data-id="${e.selectedId}"]`) : null;
      if (!div || moving || e.picking) { setTb((p) => (p ? null : p)); return; }
      const r = div.getBoundingClientRect();
      const next = { left: Math.round(r.left + r.width / 2), top: Math.round(Math.max(8, r.top - 76)) };
      setTb((p) => (p && p.left === next.left && p.top === next.top ? p : next));
    };
    place();
    const sc = scrollRef.current;
    sc?.addEventListener('scroll', place);
    window.addEventListener('resize', place);
    return () => { sc?.removeEventListener('scroll', place); window.removeEventListener('resize', place); };
  });

  // 글을 쓰는 상자·AI가 고치는 사진이 화면 밖이면 그쪽으로
  useEffect(() => {
    const id = [...e.typing][0] || [...e.imaging][0];
    if (!id) return;
    boardRef.current?.querySelector(`.page-el[data-id="${id}"]`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [e.typing.size, e.imaging.size, e.cur]);

  function begin(o: Op) {
    op.current = o;
    setMoving(true);
    const move = (ev: MouseEvent) => onMove(ev);
    const up = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      op.current = null;
      setMoving(false);
      setGuides(null);
      setRotBadge(null);
      ed.bump();
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  }
  function onMove(ev: MouseEvent) {
    const o = op.current;
    if (!o) return;
    const elm = findElement(o.id);
    if (!elm) return;
    if (!o.saved) { o.saved = true; rememberUndo(elm.id + ':' + o.kind); }
    const z = ed.zoom;
    if (o.kind === 'drag') {
      let x = Math.max(0, o.ox + (ev.clientX - o.sx) / z);
      let y = Math.max(0, o.oy + (ev.clientY - o.sy) / z);
      const vx: number[] = [], hy: number[] = [];
      if (!ev.altKey) {
        const lim = SNAP_PX / z, { xs, ys } = snapTargets(elm);
        const sx = bestSnap([x, x + elm.w / 2, x + elm.w], xs, lim);
        const sy = bestSnap([y, y + elm.h / 2, y + elm.h], ys, lim);
        if (sx) x += sx.d;
        if (sy) y += sy.d;
        [x, x + elm.w / 2, x + elm.w].forEach((p) => xs.forEach((t) => { if (Math.abs(t.v - p) < 0.5) vx.push(t.v); }));
        [y, y + elm.h / 2, y + elm.h].forEach((p) => ys.forEach((t) => { if (Math.abs(t.v - p) < 0.5) hy.push(t.v); }));
      }
      elm.x = Math.round(x); elm.y = Math.round(y);
      setGuides(vx.length || hy.length ? { elm, vx: uniq(vx), hy: uniq(hy), badges: [] } : null);
    } else if (o.kind === 'resize') {
      const dir = o.dir;
      const sx = dir.includes('e') ? 1 : dir.includes('w') ? -1 : 0;
      const sy = dir.includes('s') ? 1 : dir.includes('n') ? -1 : 0;
      const rad = ((elm.rot || 0) * Math.PI) / 180;
      let dx = (ev.clientX - o.sx) / z, dy = (ev.clientY - o.sy) / z;
      if (elm.rot) { const c = Math.cos(-rad), si = Math.sin(-rad); [dx, dy] = [dx * c - dy * si, dx * si + dy * c]; }
      let w = sx ? Math.max(30, o.ow + sx * dx) : o.ow;
      let h = sy ? Math.max(24, o.oh + sy * dy) : o.oh;
      // 모서리를 끌 때 비율 유지 — 사진은 기본으로(Shift로 풀기), 도형·글은 Shift를 눌렀을 때
      const keep = !!sx && !!sy && (elm.type === 'image') !== ev.shiftKey, ratio = o.ow / o.oh;
      if (keep) { if (w / h > ratio) h = w / ratio; else w = h * ratio; }
      const vx: number[] = [], hy: number[] = [], badges: Guides['badges'] = [];
      const snapOn = !ev.altKey && !elm.rot;
      let others: El[] = [];
      if (snapOn) {
        const lim = SNAP_PX / z, T = snapTargets(elm);
        others = T.others;
        if (sx) {
          const edge = sx > 0 ? o.ox + w : o.ox + o.ow - w;
          const se = bestSnap([edge], T.xs, lim), sw = bestSnap([w], others.map((x) => ({ v: x.w })), lim);
          if (se && (!sw || Math.abs(se.d) <= Math.abs(sw.d))) w += sx > 0 ? se.d : -se.d; else if (sw) w += sw.d;
          if (keep) h = w / ratio;
        }
        if (sy && !(keep && sx)) {
          const edge = sy > 0 ? o.oy + h : o.oy + o.oh - h;
          const se = bestSnap([edge], T.ys, lim), sh = bestSnap([h], others.map((x) => ({ v: x.h })), lim);
          if (se && (!sh || Math.abs(se.d) <= Math.abs(sh.d))) h += sy > 0 ? se.d : -se.d; else if (sh) h += sh.d;
        }
      }
      // 반대쪽 변이 제자리에 있도록 중심을 옮긴다
      const cxL = (sx * (w - o.ow)) / 2, cyL = (sy * (h - o.oh)) / 2;
      const cx = o.ox + o.ow / 2 + cxL * Math.cos(rad) - cyL * Math.sin(rad);
      const cy = o.oy + o.oh / 2 + cxL * Math.sin(rad) + cyL * Math.cos(rad);
      elm.w = Math.round(w); elm.h = Math.round(h); elm.x = Math.round(cx - w / 2); elm.y = Math.round(cy - h / 2);
      if (snapOn) {
        const T = snapTargets(elm);
        const mx = sx > 0 ? elm.x + elm.w : sx < 0 ? elm.x : null, my = sy > 0 ? elm.y + elm.h : sy < 0 ? elm.y : null;
        if (mx !== null) T.xs.forEach((t) => { if (Math.abs(t.v - mx) < 0.5) vx.push(t.v); });
        if (my !== null) T.ys.forEach((t) => { if (Math.abs(t.v - my) < 0.5) hy.push(t.v); });
        const by = elm.y + elm.h + 16 <= PAGE_H - 6 ? elm.y + elm.h + 16 : elm.y + elm.h - 16;
        const bx = elm.x + elm.w + 40 <= PAGE_W - 6 ? elm.x + elm.w + 40 : elm.x + elm.w - 40;
        if (sx && others.some((x) => Math.abs(x.w - elm.w) < 0.5)) badges.push({ text: '같은 너비', x: Math.min(Math.max(elm.x + elm.w / 2, 40), PAGE_W - 40), y: by });
        if (sy && others.some((x) => Math.abs(x.h - elm.h) < 0.5)) badges.push({ text: '같은 높이', x: bx, y: Math.min(Math.max(elm.y + elm.h / 2, 14), PAGE_H - 14) });
      }
      setGuides(vx.length || hy.length || badges.length ? { elm, vx: uniq(vx), hy: uniq(hy), badges } : null);
    } else {
      const now = (Math.atan2(ev.clientY - o.cy, ev.clientX - o.cx) * 180) / Math.PI;
      let deg = normDeg(o.base + now - o.start);
      if (ev.shiftKey) deg = normDeg(Math.round(deg / 15) * 15);
      else if (!ev.altKey) { const near = Math.round(deg / 45) * 45; if (Math.abs(deg - near) < 5) deg = normDeg(near); }
      elm.rot = deg || undefined;
      setRotBadge({ x: ev.clientX, y: ev.clientY, deg });
    }
    ed.bump();
  }
  const startDrag = (ev: RMouseEvent, elm: El) => {
    begin({ kind: 'drag', id: elm.id, sx: ev.clientX, sy: ev.clientY, ox: elm.x, oy: elm.y });
  };
  const onGrab = (ev: RMouseEvent, elm: El) => {
    if (ed.picking) return;
    ev.stopPropagation(); ev.preventDefault();
    select(elm.id);
    startDrag(ev, elm);
  };
  // ◎ 요소 선택 중에는 편집 화면 클릭을 가로채서(캡처 단계) 첨부만 한다
  const onPickCapture = (ev: RMouseEvent) => {
    // 페이지를 누르면 채팅 입력칸 등의 포커스를 놓는다 — 그래야 Delete·Ctrl+Z 가 페이지에 먹는다
    const ae = document.activeElement as HTMLElement | null;
    if (ae && ae !== document.body && !boardRef.current?.contains(ae)) ae.blur();
    if (!ed.picking) return;
    ev.preventDefault(); ev.stopPropagation();
    const d = (ev.target as HTMLElement).closest<HTMLElement>('.page-el');
    const el = d ? findElement(d.dataset.id || null) : null;
    if (!el) return;
    const i = ed.attached.indexOf(el);
    if (i >= 0) ed.attached.splice(i, 1); else ed.attached.push(el);
    if (!ev.shiftKey) ed.picking = false;
    ed.bump();
  };

  const scrollCls = 'flex flex-1 items-start overflow-auto bg-border px-06 py-08';
  if (!page) return <div className={scrollCls} />;
  const sel = e.selectedId ? page.elements.find((x) => x.id === e.selectedId) : null;
  const z = e.zoom;
  const t = 1 / z;
  const rotInside = !!sel && !sel.rot && sel.y + sel.h > PAGE_H - 44;
  return (
    <div className={scrollCls} ref={scrollRef}>
      <div className="mx-auto shrink-0 origin-top-left" style={{ width: PAGE_W * z, height: PAGE_H * z }}>
        <div className={cx('relative shrink-0 overflow-hidden rounded-sm text-[var(--gray-900)] shadow-[var(--elevation-2)]', e.picking && '[&,&_*]:!cursor-crosshair')} ref={boardRef} style={{ width: PAGE_W, height: PAGE_H, background: page.bg || PAGE_WHITE, transform: z !== 1 ? `scale(${z})` : undefined, transformOrigin: '0 0' }}
          onMouseDownCapture={onPickCapture}
          onMouseDown={(ev) => { if (ev.target === boardRef.current) select(null); }}>
          {page.elements.slice().sort((a, b) => a.z - b.z).map((elm) => <PageEl key={elm.id} elm={elm} rev={e.rev} onGrab={onGrab} />)}
          {sel && !e.picking && (
            <div className="pointer-events-none absolute z-overlay [&>div]:pointer-events-auto"
              style={{ left: sel.x, top: sel.y, width: sel.w, height: sel.h, transform: sel.rot ? `rotate(${sel.rot}deg)` : undefined }}>
              <div className={cx('absolute left-1/2 flex h-06 w-07 -translate-x-1/2 cursor-grab select-none items-center justify-center rounded-sm bg-group text-caption text-group-onPrimary', !sel.rot && sel.y < 30 ? 'top-03' : '-top-07')} title="끌어서 옮기기"
                onMouseDown={(ev) => { ev.stopPropagation(); ev.preventDefault(); startDrag(ev, sel); }}>⠿</div>
              {(sel.w * z < 48 || sel.h * z < 48 ? ['nw', 'ne', 'se', 'sw'] : ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w']).map((d) => (
                <div key={d} className={cx(RZ, RZ_AT[d])} title={d.length === 2 ? '모서리: 가로·세로 함께 (사진은 비율 유지, Shift로 풀기)' : '변: 이 방향으로만'}
                  onMouseDown={(ev) => {
                    ev.stopPropagation(); ev.preventDefault();
                    begin({ kind: 'resize', id: sel.id, dir: d, sx: ev.clientX, sy: ev.clientY, ow: sel.w, oh: sel.h, ox: sel.x, oy: sel.y });
                  }} />
              ))}
              <div className={cx(RZ, 'left-1/2 flex size-06 -translate-x-1/2 cursor-grab select-none items-center justify-center text-group [&_svg]:size-[var(--icon-sm)]', rotInside ? 'bottom-03' : '-bottom-08')} title="끌어서 회전 (Shift: 15°씩)"
                onMouseDown={(ev) => {
                  ev.stopPropagation(); ev.preventDefault();
                  const div = boardRef.current?.querySelector<HTMLElement>(`.page-el[data-id="${sel.id}"]`);
                  if (!div) return;
                  const r = div.getBoundingClientRect(), mx = r.left + r.width / 2, my = r.top + r.height / 2;
                  begin({ kind: 'rotate', id: sel.id, cx: mx, cy: my, start: (Math.atan2(ev.clientY - my, ev.clientX - mx) * 180) / Math.PI, base: sel.rot || 0 });
                }}>{!rotInside && <span className="absolute -top-04 left-1/2 h-03 w-[var(--border-width-thick)] -translate-x-1/2 bg-group" aria-hidden />}{ROT_ICON}</div>
            </div>
          )}
          {guides && (
            <div className="pointer-events-none absolute inset-0 z-tooltip">
              {guides.vx.map((v) => { const [a, b] = span(guides.elm, v, 'x'); return <div key={'x' + v} className="absolute bg-[var(--dusty-rose-500)]" style={{ left: v - t / 2, top: a, width: t, height: b - a }} />; })}
              {guides.hy.map((v) => { const [a, b] = span(guides.elm, v, 'y'); return <div key={'y' + v} className="absolute bg-[var(--dusty-rose-500)]" style={{ top: v - t / 2, left: a, height: t, width: b - a }} />; })}
              {guides.badges.map((b) => <div key={b.text} className={cx('absolute', BADGE)} style={{ left: b.x, top: b.y, transform: `translate(-50%,-50%) scale(${t})` }}>{b.text}</div>)}
            </div>
          )}
        </div>
      </div>
      {rotBadge && <div className={cx('pointer-events-none fixed z-tooltip', BADGE)} style={{ left: rotBadge.x + 16, top: rotBadge.y + 16 }}>{Math.round(rotBadge.deg)}°</div>}
      {tb && sel && <FloatingToolbar left={tb.left} top={tb.top} elm={sel} onMarkup={onMarkup} />}
    </div>
  );
}
/* 정렬선: 선이 이어주는 요소들까지만 긋는다 (페이지 기준선이면 끝에서 끝까지) */
function span(elm: El, v: number, axis: 'x' | 'y'): [number, number] {
  const { others } = snapTargets(elm);
  const hits = others.filter((o) => (axis === 'x' ? [o.x, o.x + o.w / 2, o.x + o.w] : [o.y, o.y + o.h / 2, o.y + o.h]).some((a) => Math.abs(a - v) < 0.5));
  if (!hits.length) return axis === 'x' ? [0, PAGE_H] : [0, PAGE_W];
  const all = [elm, ...hits];
  return axis === 'x'
    ? [Math.min(...all.map((o) => o.y)) - 8, Math.max(...all.map((o) => o.y + o.h)) + 8]
    : [Math.min(...all.map((o) => o.x)) - 8, Math.max(...all.map((o) => o.x + o.w)) + 8];
}

function FloatingToolbar({ left, top, elm, onMarkup }: { left: number; top: number; elm: El; onMarkup: (elm: El) => void }) {
  const attached = ed.attached.includes(elm);
  const act = (a: string) => {
    if (a === 'dup') return duplicateSelected();
    if (a === 'markup') return onMarkup(elm);
    if (a === 'ask') {
      // 선택한 요소를 대화에 첨부하고 AI 패널로 — "◎ 요소 선택"을 거치지 않는 지름길
      const i = ed.attached.indexOf(elm);
      if (i >= 0) ed.attached.splice(i, 1); else { ed.attached.push(elm); ed.dockOpen = true; }
      ed.bump();
      if (i < 0) setTimeout(() => document.querySelector<HTMLTextAreaElement>('[data-showcase] [data-chat-input]')?.focus(), 0);
      return;
    }
    rememberUndo();
    if (a === 'del') return deleteElement(elm.id);
    if (a === 'front') bringToFront(elm);
    else if (a === 'back') sendToBack(elm);
    ed.bump();
  };
  return (
    <div className="fixed z-base flex -translate-x-1/2 gap-xs rounded-lg border border-border bg-surface-raised p-02 shadow-[var(--elevation-2)] [&_.btn]:whitespace-nowrap" role="toolbar" aria-label="선택한 요소" style={{ left, top }} onMouseDown={(e) => e.stopPropagation()}>
      {elm.type === 'image' && <Button size="sm" variant="ghost" onClick={() => act('markup')}><IcPen size={16} />표시하고 고치기</Button>}
      <Button size="sm" variant="ghost" onClick={() => act('dup')}>복제</Button>
      <Button size="sm" variant="ghost" onClick={() => act('front')}>앞으로</Button>
      <Button size="sm" variant="ghost" onClick={() => act('back')}>뒤로</Button>
      <Button size="sm" variant="ghost" className="hover:text-error" onClick={() => act('del')}><IcClose size={16} />삭제</Button>
      <Button size="sm" variant="ghost" aria-pressed={attached} onClick={() => act('ask')}><IcChat size={16} />{attached ? '첨부 빼기' : 'AI에게'}</Button>
    </div>
  );
}
